import {hsl} from "polished"
import {ImmerReducer} from "use-immer"
import type {Draft} from "immer"
import {Budgets} from "@irati/wallet/hooks/budget"
import {Budget, BudgetUnassigned} from "@irati/types"
import {SimpleTag} from "./useTags"
import omitEmpty from "@irati/utils/omitEmpty"
import {assertUnreachable} from "../../../utils/assertUnreachable"

type EditableCategory = Prettify<Budget & Editable>
type NewCategory = WithPartial<EditableCategory, "tag">

export const MAX_BUDGETS = 30

export interface Validation {
  id: string
  values?: {
    type: "name" | "colors"
    names: string[]
  }
}

export type EditableBudget = EditableCategory | NewCategory

export interface EditableBudgetCategories {
  unassigned: BudgetUnassigned
  categories: EditableCategory[]
  newCategories: NewCategory[]
}

interface Editable {
  max: number
  min: number
  changes?: {
    name?: boolean
    color?: boolean
    tag?: boolean
    amount?: {
      isIncrease: boolean
      delta: number
    }
  }
}

type Changes = NonNullable<EditableBudget["changes"]>

export interface State {
  /**
   * internal data for calcuations, don't consume from state._internal
   */
  _internal?: {
    budgets: Budgets
    canManageCategories: boolean
  }
  /**
   * if the upstream budget categories change value, this flag will become
   * `true` and it is unlikely you want the user to proceed at this point. You should
   * likely restart the flow or prompt the user to prevent confusing downstream
   * errors
   */
  outdated: boolean
  ignoredOutdated: boolean
  /** true if any form field been altered from it's original value */
  dirty: boolean
  /** true if the user has entered 'editing mode', it does not mean a change has happened, for that see `dirty` */
  editing: boolean
  budgets?: EditableBudgetCategories
  /** the data is currently being submitted */
  submitting: boolean
  formErrors?: Validation[]
  /** an error string returned from the fetch call */
  updateError?: PBError
}

interface Update<A> {
  uuid: string
  value: A
}

export type Action =
  | {type: "budgetDataUpdated"; data: {budgets: Budgets; canManageCategories: boolean}}
  | {type: "startEdit"}
  | {type: "finishEdit"}
  | {type: "confirmCancel"}
  | {type: "formErrors"; data: Validation[]}
  | {type: "addBudget"}
  | {type: "removeBudget"; data: string}
  | {type: "setColor"; data: Update<string>}
  | {type: "setAmount"; data: Update<number>}
  | {type: "setName"; data: Update<string>}
  | {type: "setTag"; data: Update<SimpleTag>}
  | {type: "startMutation"}
  | {type: "endMutation"; data: {error?: PBError}}
  | {type: "outdatedIgnored"}

export const initialState: State = {
  dirty: false,
  outdated: false,
  ignoredOutdated: false,
  editing: false,
  submitting: false
}

export const reducer: ImmerReducer<State, Action> = (draft, action) => {
  if (action.type.startsWith("set")) {
    draft.dirty = true
    delete draft.updateError
    delete draft.formErrors
  }

  switch (action.type) {
    case "startEdit":
      draft.editing = true
      break

    case "finishEdit":
    case "confirmCancel": {
      const {_internal} = draft

      return {
        ...initialState,
        ...(_internal?.budgets && {
          budgets: mapBudgetsToEditable(_internal)
        }),
        _internal
      }
    }

    case "formErrors":
      draft.formErrors = action.data
      break

    case "outdatedIgnored":
      draft.ignoredOutdated = true
      break

    case "budgetDataUpdated": {
      if (draft.editing && !draft.submitting) {
        draft.outdated = true
      } else {
        draft._internal = action.data
        draft.budgets = mapBudgetsToEditable(action.data)

        if (action.data.canManageCategories) {
          // if an admin has no budgets, we want to jump them straight into editing
          draft.editing = Boolean(draft.budgets.categories.length === 0)
        }
      }

      break
    }

    case "setColor": {
      const {value} = action.data
      const category = findCategory(draft, action.data)
      const original = findOriginal(draft, action.data)?.metadata

      category.metadata = {
        color: value,
        // needed for the schema but not used
        createdAt: "",
        updatedAt: "",
        createdBy: "",
        updatedBy: ""
      }

      setOrClearChanges(category, "color", {
        shouldUpdate: original?.color !== value,
        updatedValue: true
      })

      break
    }

    case "setTag": {
      const {value} = action.data
      const category = findCategory(draft, action.data)
      const original = findOriginal(draft, action.data)?.tag

      category.tag = value

      setOrClearChanges(category, "tag", {
        shouldUpdate: value.uuid !== original?.uuid,
        updatedValue: true
      })

      break
    }

    case "setName": {
      const {value} = action.data
      const category = findCategory(draft, action.data)
      const original = findOriginal(draft, action.data)?.name

      category.name = value

      setOrClearChanges(category, "name", {
        shouldUpdate: value !== original,
        updatedValue: true
      })

      break
    }

    case "setAmount": {
      const {value} = action.data
      const category = findCategory(draft, action.data)

      if (!draft.budgets) return

      const {unassigned} = draft.budgets

      const currentAmount = category.amount
      const newAmount = value
      const diff = newAmount - currentAmount
      const isIncrease = diff > 0
      const total = draft._internal?.budgets.total ?? 0
      const overbuget = isIncrease && diff > unassigned.amount

      if (overbuget) {
        category.amount = unassigned.amount + category.amount
        category.percentage = (category.amount / total) * 100

        unassigned.amount = 0
        unassigned.percentage = 0
      } else {
        category.amount = newAmount
        category.percentage = (category.amount / total) * 100

        unassigned.amount -= diff
        unassigned.percentage = (unassigned.amount / total) * 100
      }

      category.max = unassigned.amount + category.amount

      const existingCategoryAmount = findOriginal(draft, action.data)?.amount

      if (existingCategoryAmount !== undefined) {
        const delta = category.amount - existingCategoryAmount
        setOrClearChanges(category, "amount", {
          shouldUpdate: Boolean(delta),
          updatedValue: {isIncrease: delta > 0, delta}
        })
      }

      break
    }

    case "addBudget": {
      const idx = (draft.budgets?.categories.length ?? 0) + (draft.budgets?.newCategories?.length ?? 0)

      draft.dirty = true
      delete draft.updateError
      delete draft.formErrors

      draft.budgets?.newCategories.push({
        uuid: Math.random().toString(),
        amount: 0,
        name: "",
        percentage: 0,
        metadata: {
          color: generateColor(idx),
          // not needed, could have a new schema type, but makes things easier to have the same metadata type
          createdAt: "",
          updatedAt: "",
          createdBy: "",
          updatedBy: ""
        },
        currency: draft.budgets?.categories[0]?.currency,
        max: draft.budgets?.unassigned.amount,
        min: 0
      })

      break
    }

    case "removeBudget": {
      const uuid = action.data
      const categoryIdx = draft.budgets?.newCategories.findIndex(byId(uuid)) ?? -1

      if (!draft.budgets || categoryIdx === -1) return

      const {unassigned, newCategories} = draft.budgets
      const {amount} = newCategories[categoryIdx] ?? {}
      const total = draft._internal?.budgets.total ?? 0

      unassigned.amount += amount
      unassigned.percentage = (unassigned.amount / total) * 100

      draft.budgets?.newCategories.splice(categoryIdx, 1)

      break
    }

    case "startMutation":
      draft.submitting = true
      delete draft.updateError

      break

    case "endMutation":
      draft.submitting = false
      draft.updateError = action.data.error

      break

    default:
      assertUnreachable(action)
  }
  return undefined
}

function byId<A extends {uuid: string}>(uuid: string) {
  return (category: A) => {
    return category.uuid === uuid
  }
}

function findCategory(draft: Draft<State>, {uuid}: {uuid: string}): Draft<EditableBudget> {
  const budget = draft.budgets?.newCategories.find(byId(uuid)) || draft.budgets?.categories.find(byId(uuid))
  if (!budget) throw new Error(`Could not find Budget ${uuid}`)
  return budget
}

function findOriginal(draft: Draft<State>, {uuid}: {uuid: string}): Draft<Budget> | undefined {
  return draft._internal?.budgets.budgets.categories.find(byId(uuid))
}

function setOrClearChanges<Key extends keyof Changes>(
  category: Draft<EditableBudget>,
  key: Key,
  {
    shouldUpdate: didUpdate,
    updatedValue: update
  }: {
    shouldUpdate: boolean
    updatedValue: Changes[Key]
  }
): void {
  category.changes ??= {}
  if (didUpdate) {
    category.changes[key] = update
  } else {
    delete category.changes[key]
    category.changes = omitEmpty(category.changes)
  }
}

function mapBudgetsToEditable({budgets: {budgets}, canManageCategories}: NonNullable<State["_internal"]>): EditableBudgetCategories {
  return {
    newCategories: [],
    unassigned: budgets.unassigned,
    categories: budgets.categories.map((budget) => ({
      ...budget,
      min: canManageCategories ? 0 : budget.amount,
      max: budgets.unassigned.amount + budget.amount
    }))
  }
}

/**
 * hsl colors rotate hue around 360 degrees (i.e 0 and 360 are the same)
 * This allows us to incrementally shift the color hue by a fixed amount
 * such that if a client just settles for autogenerated colors, they will
 * all be visually distinct
 */
function generateColor(n: number): string {
  const step = Math.floor(360 / MAX_BUDGETS) * 2.2
  const angle = step * n
  return hsl(angle, 0.8, 0.5)
}
